/** Sidebar menü düzeni — gruplar, sıra, sürükle-bırak (localStorage). */

export const LAYOUT_STORAGE_KEY = "mbs_sidebar_layout_v1";

function uid() {
    return `g_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
}

/** @typedef {{ id: string, label: string, custom?: boolean }} MenuGroup */
/** @typedef {{ groups: MenuGroup[], items: Record<string, string[]> }} MenuLayout */

export function buildDefaultMenuLayout(catalogGroups) {
    /** @type {MenuLayout} */
    const layout = { groups: [], items: {} };
    for (const g of catalogGroups || []) {
        layout.groups.push({
            id: g.key,
            label: g.label,
            custom: false,
        });
        layout.items[g.key] = (g.items || []).map((it) => it.path);
    }
    return layout;
}

export function loadMenuLayout(catalogGroups) {
    const fallback = buildDefaultMenuLayout(catalogGroups);
    try {
        const raw = localStorage.getItem(LAYOUT_STORAGE_KEY);
        if (!raw) return syncLayoutWithCatalog(fallback, catalogGroups);
        const parsed = JSON.parse(raw);
        if (!parsed?.groups?.length || !parsed.items) {
            return syncLayoutWithCatalog(fallback, catalogGroups);
        }
        return syncLayoutWithCatalog(parsed, catalogGroups);
    } catch (_) {
        return syncLayoutWithCatalog(fallback, catalogGroups);
    }
}

export function saveMenuLayout(layout) {
    try {
        localStorage.setItem(LAYOUT_STORAGE_KEY, JSON.stringify(layout));
    } catch (_) {
        /* ignore */
    }
}

/** Yeni modüller ve eksik path'leri varsayılan gruba ekler. */
export function syncLayoutWithCatalog(layout, catalogGroups) {
    const defaultLayout = buildDefaultMenuLayout(catalogGroups);
    const pathToDefaultGroup = new Map();
    for (const g of catalogGroups || []) {
        for (const it of g.items || []) {
            pathToDefaultGroup.set(it.path, g.key);
        }
    }

    const groups = [...(layout.groups || [])];
    const groupIds = new Set(groups.map((g) => g.id));
    const items = { ...(layout.items || {}) };

    for (const g of groups) {
        if (!items[g.id]) items[g.id] = [];
    }

    for (const dg of defaultLayout.groups) {
        if (!groupIds.has(dg.id)) {
            groups.push({ ...dg });
            items[dg.id] = items[dg.id] || [];
            groupIds.add(dg.id);
        }
    }

    const placed = new Set();
    for (const gid of Object.keys(items)) {
        items[gid] = (items[gid] || []).filter((p) => {
            if (placed.has(p)) return false;
            placed.add(p);
            return true;
        });
    }

    for (const g of catalogGroups || []) {
        for (const it of g.items || []) {
            if (placed.has(it.path)) continue;
            const target = pathToDefaultGroup.get(it.path) || g.key;
            if (!items[target]) items[target] = [];
            items[target].push(it.path);
            placed.add(it.path);
        }
    }

    return { groups, items };
}

export function resetMenuLayout(catalogGroups) {
    const layout = buildDefaultMenuLayout(catalogGroups);
    saveMenuLayout(layout);
    return layout;
}

function cloneLayout(layout) {
    return {
        groups: layout.groups.map((g) => ({ ...g })),
        items: Object.fromEntries(
            Object.entries(layout.items).map(([k, v]) => [k, [...v]])
        ),
    };
}

function removePathFromAll(layout, path) {
    for (const gid of Object.keys(layout.items)) {
        layout.items[gid] = layout.items[gid].filter((p) => p !== path);
    }
}

export function moveMenuItem(layout, path, toGroupId, toIndex = null) {
    if (!path || !toGroupId) return layout;
    const next = cloneLayout(layout);
    if (!next.items[toGroupId]) next.items[toGroupId] = [];
    removePathFromAll(next, path);
    const list = next.items[toGroupId];
    const idx =
        toIndex == null || toIndex < 0 || toIndex > list.length
            ? list.length
            : toIndex;
    list.splice(idx, 0, path);
    saveMenuLayout(next);
    return next;
}

export function reorderMenuItemInGroup(layout, groupId, fromIndex, toIndex) {
    const next = cloneLayout(layout);
    const list = next.items[groupId];
    if (!list || fromIndex < 0 || fromIndex >= list.length) return layout;
    const [item] = list.splice(fromIndex, 1);
    const idx = Math.max(0, Math.min(toIndex, list.length));
    list.splice(idx, 0, item);
    saveMenuLayout(next);
    return next;
}

export function reorderMenuGroups(layout, fromIndex, toIndex) {
    const next = cloneLayout(layout);
    const { groups } = next;
    if (fromIndex < 0 || fromIndex >= groups.length) return layout;
    const [g] = groups.splice(fromIndex, 1);
    const idx = Math.max(0, Math.min(toIndex, groups.length));
    groups.splice(idx, 0, g);
    saveMenuLayout(next);
    return next;
}

export function addCustomMenuGroup(layout, label) {
    const name = (label || "").trim() || "Yeni Grup";
    const id = uid();
    const next = cloneLayout(layout);
    next.groups.push({ id, label: name, custom: true });
    next.items[id] = [];
    saveMenuLayout(next);
    return next;
}

export function renameMenuGroup(layout, groupId, label) {
    const name = (label || "").trim();
    if (!name) return layout;
    const next = cloneLayout(layout);
    const g = next.groups.find((x) => x.id === groupId);
    if (!g) return layout;
    g.label = name;
    saveMenuLayout(next);
    return next;
}

export function removeCustomMenuGroup(layout, groupId) {
    const next = cloneLayout(layout);
    const g = next.groups.find((x) => x.id === groupId);
    if (!g?.custom) return layout;
    const orphan = [...(next.items[groupId] || [])];
    delete next.items[groupId];
    next.groups = next.groups.filter((x) => x.id !== groupId);
    const fallback = next.groups.find((x) => x.id === "genel") || next.groups[0];
    if (fallback && orphan.length) {
        next.items[fallback.id] = [...(next.items[fallback.id] || []), ...orphan];
    }
    saveMenuLayout(next);
    return next;
}

export function layoutToCustomizerGroups(layout, navCatalog) {
    return (layout.groups || []).map((g) => ({
        key: g.id,
        label: g.label,
        custom: Boolean(g.custom),
        items: (layout.items[g.id] || [])
            .map((path) => {
                const meta = navCatalog.get(path);
                if (!meta) return null;
                return { path, title: meta.title, icon: meta.icon };
            })
            .filter(Boolean),
    }));
}
